import * as React from 'react';
import {
    Chip,
    DialogContentText,
    Grid2 as Grid,
    InputLabel,
    Switch,
    Tab,
    Tabs,
    Typography,
    Step,
    StepButton,
    Stepper, Divider,
} from '@mui/material';
import {
    Button,
    Dropzone,
    Metadata,
    TextField,
    NumberField,
    ScheduledEntityComponent,
    RecurringEntityComponent,
} from '../common';
import {
    formatMomentDate,
    getDurationMenus,
    RecurringEntity,
    StyledAccordion,
    StyledAccordionDetails,
    StyledAccordionSummary,
} from '../../utils';
import { default as moment, Moment } from 'moment';
import {
    AddOutlined,
    ChevronLeftOutlined,
    ChevronRightOutlined,
    ExpandMore,
} from '@mui/icons-material';
import { styled } from '@mui/styles';
import { SelectSegmentDialog } from '../segment';
import { RecurringResetTimerType, TimerType } from '../../enums';
import { TabContext, TabPanel } from '@mui/lab';

interface Props {
    readonly styled: StyledProps;
    readonly type: 'Create' | 'Edit';
    readonly items: Entities.EconomyItem[];
    readonly onCreate?: (
        displayName: string,
        metadata: string,
        productId: string,
        purchaseLimit: number,
        segmentIds: string[],
        items: string[],
        virtualCurrencyPrices: string | null,
        startTimestamp: number | null,
        endTimestamp: number | null,
        timerType: TimerType,
        recurringResetTimerType: RecurringResetTimerType | null,
        recurringStart: number | null,
        recurringDuration: number | null,
        recurringDailyDays: number[] | null,
        recurringWeeklyDayStart: number | null
    ) => void;
    readonly onEdit?: (
        id: string,
        displayName: string,
        metadata: string,
        productId: string,
        purchaseLimit: number,
        selectedSegmentIds: string[],
        items: string[],
        virtualCurrencyPrices: string | null,
        startTimestamp: number | null,
        endTimestamp: number | null,
        timerType: TimerType,
        recurringResetTimerType: RecurringResetTimerType | null,
        recurringStart: number | null,
        recurringDuration: number | null,
        recurringDailyDays: number[] | null,
        recurringWeeklyDayStart: number | null
    ) => void;
    readonly onCancel: () => void;
    readonly item?: Entities.EconomyStoreItem;
    readonly segments: Entities.Segment[];
    readonly currencies: Entities.Currency[];
}

type Step = 'Detail' | 'Schedule' | 'Confirm';
interface State {
    readonly step: Step;
    readonly selectSegmentDialogOpen: boolean;
    readonly id: string;
    readonly displayName: string;
    readonly metadata: string;
    readonly purchaseLimit: number;
    readonly items: string[];
    readonly file: File | null;
    readonly start: Moment;
    readonly end: Moment;
    readonly isRealMoney: boolean;
    readonly productId: string;
    readonly virtualCurrencyPrices: string;
    readonly selectedSegmentIds: string[];
    readonly timerType: TimerType;
    readonly recurringResetTimerType: RecurringResetTimerType;
    readonly recurringStart: Moment;
    readonly recurringDuration: number;
    readonly recurringDailyDays: number[] | null;
    readonly recurringWeeklyDayStart: number;
}

export class CreateEconomyStoreItemComponent extends React.Component<Props, State> {
    state = this.initState();

    componentDidMount() {
        const { item } = this.props;
        if (!item) {
            return;
        }
        this.setState(this.initStateFromItem(item));
    }

    componentDidUpdate(prevProps: Props) {
        const { item } = this.props;
        if (!item || !prevProps.item || item.id === prevProps.item.id) {
            return;
        }

        this.setState(this.initStateFromItem(item));
    }

    render() {
        const { segments } = this.props;
        const { step, selectSegmentDialogOpen, selectedSegmentIds } = this.state;

        let detailContent: React.JSX.Element;
        let detailButtons: React.JSX.Element;

        let activeStep;

        switch (step) {
            case 'Detail':
                activeStep = 0;
                detailContent = this.renderDetailContent();
                detailButtons = this.renderDetailButtons();
                break;
            case 'Schedule':
                activeStep = 1;
                detailContent = this.renderScheduleContent();
                detailButtons = this.renderScheduleButtons();
                break;
            case 'Confirm':
                activeStep = 3;
                detailContent = this.renderConfirmContent();
                detailButtons = this.renderConfirmButtons();
                break;
            default:
                activeStep = -1;
                detailContent = <></>;
                detailButtons = <></>;
                break;
        }

        const availableToSelectSegments = segments.filter(s => !selectedSegmentIds.some(id => s.id === id));

        return (
            <>
                <Grid container={true} spacing={1} style={{ width: '90%', margin: 'auto' }}>
                    <Grid size={12} sx={{ display: { xs: 'none', sm: 'block' } }}>
                        <Stepper
                            alternativeLabel={true}
                            nonLinear={true}
                            activeStep={activeStep}
                        >
                            <Step key={'Details'} completed={activeStep > 0}>
                                <StepButton onClick={this.onDetailStepSet} >
                                    {'Details'}
                                </StepButton>
                            </Step>
                            <Step key={'Schedule'} completed={activeStep > 1}>
                                <StepButton onClick={this.onScheduleStepSet} >
                                    {'Schedule'}
                                </StepButton>
                            </Step>
                            <Step key={'Confirm'} completed={activeStep > 3}>
                                <StepButton onClick={this.onConfirmStepSet} >
                                    {'Confirm'}
                                </StepButton>
                            </Step>
                        </Stepper>
                    </Grid>
                    <Grid size={12} style={{height: 30}}/>
                    <Grid size={12}>
                        {detailContent}
                    </Grid>
                    <Grid size={12} style={{height: 30}}/>
                    <Grid size={12} style={{
                        display: 'grid',
                        justifyContent: 'flex-end',
                        gridAutoFlow: 'column',
                        gridColumnGap: 10,
                    }}>
                        {detailButtons}
                    </Grid>
                </Grid>
                <SelectSegmentDialog
                    styled={this.props.styled}
                    segments={availableToSelectSegments}
                    onSelectSegment={this.onSelectSegment}
                    title={"Select Segments"}
                    onClose={this.closeSelectSegmentDialog}
                    open={selectSegmentDialogOpen}
                />
            </>
        );
    }

    protected renderDetailContent(): React.JSX.Element {
        const { items, segments } = this.props;
        const { displayName, metadata, isRealMoney, productId, purchaseLimit, selectedSegmentIds } = this.state;

        const segmentElements: React.JSX.Element[] = [];
        selectedSegmentIds.forEach(segmentId => {
            const segment = segments.find(s => s.id === segmentId);
            if (!segment) {
                return;
            }
            const onRemoveSelected = () => this.setState({ selectedSegmentIds: selectedSegmentIds.filter(id => id !== segmentId)});
            segmentElements.push(
                <Grid size={{ xs:12, sm:6, md:4, lg:3 }}>
                    <Chip
                        color={'secondary'}
                        label={segment.name}
                        style={{ width: '100%', backgroundColor: segment.color, color: 'white' }}
                        onDelete={onRemoveSelected}
                    />
                </Grid>
            );
        });

        const availableToSelectSegments = segments.filter(s => !selectedSegmentIds.some(id => s.id === id));

        return (
            <>
                <Grid container={true} spacing={0}>
                    <Grid size={12}>
                        <TextField
                            label="Display Name"
                            value={displayName}
                            variant="outlined"
                            fullWidth={true}
                            onChange={this.onChangeDisplayName}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Dropzone
                            label="Image"
                            accept={{
                                "image/jpeg": ['.jpeg'],
                                "image/jpg": ['.jpg'],
                                "image/png": ['.png'],
                            }}
                            multiple={false}
                            text={'Drop image file'}
                            onDropFile={this.handleDrop}
                            description={'Upload image (JPEG, JPG or PNG)'}
                        />
                    </Grid>
                    <Grid size={12}>
                        <NumberField
                            label="Purchase Limit"
                            value={purchaseLimit}
                            variant="outlined"
                            fullWidth={true}
                            onChange={this.onChangePurchaseLimit}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Metadata
                            styled={this.props.styled}
                            label="Metadata"
                            text={metadata}
                            onChange={this.onChangeMetadata}
                        />
                    </Grid>
                    <Grid size={12}>
                        <Divider style={{marginTop: 20}}/>
                    </Grid>
                    <Grid size={12}>
                        <InputLabel>PRICE</InputLabel>
                        <Grid size={12}>
                            <div style={{display: 'grid', gridAutoFlow: 'column', gridColumnGap: 10, justifyContent: 'flex-end'}}>
                                <Typography variant="caption" style={{margin: 'auto'}}>Virtual Currency</Typography>
                                <Switch
                                    checked={isRealMoney}
                                    onChange={this.changeIsRealMoney}
                                    color="primary"
                                    inputProps={{ 'aria-label': 'primary checkbox' }}
                                />
                                <Typography variant="caption" style={{margin: 'auto'}}>Real Money</Typography>
                            </div>
                        </Grid>
                        {isRealMoney && (
                            <>
                                <Grid size={12}>
                                    <TextField
                                        label="Store Product ID"
                                        value={productId}
                                        variant="outlined"
                                        fullWidth={true}
                                        onChange={this.onChangeProductId}
                                    />
                                </Grid>
                            </>
                        )}
                        {!isRealMoney && (
                            <Grid size={{ xs:12 }} />
                        )}
                    </Grid>
                    {segments.length > 0 && (
                        <>
                            <Grid size={12}>
                                <Divider style={{marginTop: 20}}/>
                            </Grid>
                            <Grid size={12}>
                                <InputLabel>SEGMENTATION</InputLabel>
                                <Grid container={true} justifyContent="center" spacing={2}>
                                    <Grid size={12}>
                                        <StyledButton text="Add Segment" icon={AddOutlined} onClick={this.openSelectSegmentDialog} disabled={availableToSelectSegments.length === 0}/>
                                    </Grid>
                                    {segmentElements}
                                </Grid>
                            </Grid>
                        </>
                    )}
                    {items.length > 0 && (
                        <Grid size={12}>
                            <StyledAccordion defaultExpanded={true}>
                                <StyledAccordionSummary>
                                    ITEMS
                                </StyledAccordionSummary>
                                <StyledAccordionDetails/>
                            </StyledAccordion>
                        </Grid>
                    )}
                </Grid>
            </>
        );
    }

    protected renderDetailButtons(): React.JSX.Element {
        return (
            <>
                <Button text="Next" icon={ChevronRightOutlined} iconPlacement={'end'} onClick={this.onScheduleStepSet} variant="contained"/>
            </>
        );
    }

    protected onDetailStepSet = () => this.setState({ step: 'Detail'});

    protected renderScheduleContent(): React.JSX.Element {
        const { themeMode } = this.props.styled;
        const {
            timerType,
            start,
            end,
            recurringResetTimerType,
            recurringStart,
            recurringDuration,
            recurringWeeklyDayStart,
            recurringDailyDays
        } = this.state;

        return (
            <TabContext value={timerType.toString()}>
                <Tabs
                    value={timerType.toString()}
                    onChange={(event, value) => this.setState({ timerType: parseInt(value) })}
                >
                    <Tab label="Never" value={"0"}/>
                    <Tab label="Scheduled" value={"1"}/>
                    <Tab label="Recurring" value={"2"}/>
                </Tabs>
                <StyledTabPanel value={"0"}>
                    <DialogContentText>
                        The item will start immediately after you complete the setup and never reset.
                    </DialogContentText>
                </StyledTabPanel>
                <StyledTabPanel value={"1"}>
                    <ScheduledEntityComponent
                        title={'Scheduled items only run once at the selected date and time in the future.'}
                        styled={this.props.styled}
                        start={start}
                        handleStartChange={this.handleStartChange}
                        end={end}
                        handleEndChange={this.handleEndChange}
                    />
                </StyledTabPanel>
                <StyledTabPanel value={"2"}>
                    <RecurringEntityComponent
                        title={'Recurring items will continue to run until you stop them or a specific end date you set is reached.'}
                        styled={this.props.styled}
                        start={start}
                        handleStartChange={this.handleStartChange}
                        end={end}
                        handleEndChange={this.handleEndChange}
                        recurringResetTimerType={recurringResetTimerType}
                        handleRecurringResetTimerTypeChange={this.handleRecurringResetTimerTypeChange}
                        recurringStart={recurringStart}
                        handleRecurringStartChange={this.handleRecurringStartChange}
                        recurringDuration={recurringDuration}
                        handleRecurringDurationChange={this.handleRecurringDurationChange}
                        recurringDailyDays={recurringDailyDays}
                        handleRecurringDailyDaysChange={this.handleRecurringDailyDaysChange}
                        recurringWeeklyDayStart={recurringWeeklyDayStart}
                        handleRecurringWeeklyDayStartChange={this.handleRecurringWeeklyDayStartChange}
                    />
                </StyledTabPanel>
            </TabContext>
        );
    }

    protected renderScheduleButtons(): React.JSX.Element {
        return (
            <>
                <Button text="Back" icon={ChevronLeftOutlined} onClick={this.onDetailStepSet} variant="contained" color="secondary"/>
                <Button text="Next" icon={ChevronRightOutlined} iconPlacement={'end'} onClick={this.onConfirmStepSet} variant="contained"/>
            </>
        );
    }

    protected onScheduleStepSet = () => this.setState({ step: 'Schedule'});

    protected renderConfirmContent(): React.JSX.Element {
        const { segments } = this.props;
        const {
            displayName,
            metadata,
            start,
            end,
            timerType,
            recurringResetTimerType,
            recurringStart,
            recurringDuration,
            recurringDailyDays,
            recurringWeeklyDayStart,
            selectedSegmentIds,
        } = this.state;

        const recurringStartTimestamp = recurringStart.clone().utc().valueOf() - recurringStart.clone().utc().startOf('day').valueOf();;
        const recurringEntity = new RecurringEntity({
            createdTimestamp: 0,
            startTimestamp: 0,
            endTimestamp: 0,
            timerType,
            recurringResetTimerType,
            recurringStart: recurringStartTimestamp,
            recurringDuration: recurringDuration * 60 * 1000,
            recurringWeeklyDayStart,
            recurringDailyDays: recurringDailyDays || [],
        });

        const segmentElements: React.JSX.Element[] = [];
        selectedSegmentIds.forEach(segmentId => {
            const segment = segments.find(s => s.id === segmentId);
            if (!segment) {
                return;
            }
            segmentElements.push(
                <Chip
                    color={'secondary'}
                    label={segment.name}
                    style={{ backgroundColor: `${segment.color}`, color: 'white', margin: '0px 4px' }}
                />
            );
        });
        return (
            <Grid container={true} justifyContent="center" spacing={4}>
                <Grid size={12}>
                    <StyledAccordion defaultExpanded={true}>
                        <StyledAccordionSummary expandIcon={<ExpandMore />}>
                            Details
                        </StyledAccordionSummary>
                        <StyledAccordionDetails>
                            <Grid container={true} justifyContent="center" spacing={1}>
                                {segmentElements.length > 0 && (
                                    <Grid size={12}>
                                        {segmentElements}
                                    </Grid>
                                )}
                                <Grid size={{ xs: 12, sm: 3 }}>
                                    <strong>Name</strong>
                                </Grid>
                                <Grid size={{ xs: 12, sm: 9 }}>
                                    {displayName}
                                </Grid>
                                {timerType === TimerType.Scheduled && (
                                    <>
                                        <Grid size={{ xs: 12, sm: 3 }}>
                                            <strong>Start</strong>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 9 }}>
                                            {formatMomentDate(start)}
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 3 }}>
                                            <strong>End</strong>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 9 }}>
                                            {formatMomentDate(end)}
                                        </Grid>
                                    </>
                                )}
                                {timerType === TimerType.Recurring && (
                                    <>
                                        <Grid size={{ xs: 12, sm: 3 }}>
                                            <strong>Start</strong>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 9 }}>
                                            {formatMomentDate(start, 'LL')}
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 3 }}>
                                            <strong>End</strong>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 9 }}>
                                            {formatMomentDate(end, 'LL')}
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 3 }}>
                                            <strong>Repeat</strong>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 9 }}>
                                            {recurringEntity.getRecurringText()}
                                        </Grid>
                                    </>
                                )}
                            </Grid>
                        </StyledAccordionDetails>
                    </StyledAccordion>
                </Grid>
                {metadata !== '' && (
                    <Grid size={12}>
                        <StyledAccordion defaultExpanded={true}>
                            <StyledAccordionSummary expandIcon={<ExpandMore />}>
                                Metadata
                            </StyledAccordionSummary>
                            <StyledAccordionDetails>
                                <Metadata
                                    styled={this.props.styled}
                                    text={metadata}
                                    readOnly={true}
                                    style={{ width: '100%' }}
                                    keyValueContainerStyle={{ boxShadow: 'none' }}
                                    showBox={false}
                                />
                            </StyledAccordionDetails>
                        </StyledAccordion>
                    </Grid>
                )}
            </Grid>
        );
    }

    protected renderConfirmButtons(): React.JSX.Element {
        return (
            <>
                <Button text="Back" icon={ChevronLeftOutlined} onClick={this.onScheduleStepSet} variant="contained" color="secondary" />
                <Button text="Confirm" onClick={this.onCreate} variant="contained"/>
            </>
        );
    }

    protected onConfirmStepSet = () => this.setState({ step: 'Confirm'});

    private initState(): State {
        return {
            step: 'Detail',
            id: '',
            displayName: '',
            metadata: '',
            purchaseLimit: 0,
            items: [],
            file: null,
            isRealMoney: true,
            productId: '',
            virtualCurrencyPrices: '',
            selectedSegmentIds: [],
            selectSegmentDialogOpen: false,
            start: moment.parseZone(),
            end: moment.parseZone().add(1, 'day'),
            timerType: TimerType.None,
            recurringResetTimerType: RecurringResetTimerType.Daily,
            recurringStart: moment.parseZone(),
            recurringDuration: getDurationMenus()['1hour'].value,
            recurringDailyDays: null,
            recurringWeeklyDayStart: 0,
        };
    }

    private initStateFromItem(item: Entities.EconomyStoreItem): State {
        const { type } = this.props;
        const {
            id,
            displayName,
            metadata,
            productId,
            purchaseLimit,
            items,
            virtualCurrencyPrices,
            segmentIds,
            startTimestamp,
            endTimestamp,
            recurringResetTimerType,
            recurringDuration,
            recurringWeeklyDayStart,
            recurringDailyDays,
        } = item;

        const start = type === 'Edit' && startTimestamp !== null ? moment(startTimestamp) : moment.parseZone();
        const end = type === 'Edit' && endTimestamp !== null ? moment(endTimestamp) : moment.parseZone().add(1, 'day');
        const recurringStart = type === 'Edit' ? moment(item.recurringStart) : moment.parseZone();
        const selectedSegmentIds = type === 'Edit' ? segmentIds : [];
        const timerType = item.timerType || 0;

        return {
            step: 'Detail',
            id,
            displayName: displayName || '',
            metadata: metadata || '',
            purchaseLimit: purchaseLimit || 0,
            items: items || [],
            file: null,
            start,
            end,
            isRealMoney: productId !== '',
            productId: productId || '',
            virtualCurrencyPrices: virtualCurrencyPrices || '',
            selectedSegmentIds,
            selectSegmentDialogOpen: false,
            timerType,
            recurringResetTimerType,
            recurringStart,
            recurringDuration,
            recurringDailyDays,
            recurringWeeklyDayStart,
        };
    }

    private onCreate = async () => {
        const { type, onCreate, onEdit } = this.props;
        const {
            id,
            displayName,
            metadata,
            productId,
            purchaseLimit,
            items,
            isRealMoney,
            virtualCurrencyPrices,
            start,
            end,
            timerType,
            recurringStart,
            selectedSegmentIds
        } = this.state;

        start.set('seconds', 0);
        start.set('millisecond', 0);
        recurringStart.set('seconds', 0);
        recurringStart.set('millisecond', 0);
        end.set('seconds', 0);
        end.set('millisecond', 0);

        let startTimestamp: number | null = null;
        let endTimestamp: number | null = null;
        let recurringResetTimerType: number | null = null;
        let recurringStartTimestamp: number | null = null;
        let recurringDuration: number | null = null;
        let recurringDailyDays: number[] | null = null;
        let recurringWeeklyDayStart: number | null = null;
        switch (timerType) {
            case TimerType.Scheduled:
                startTimestamp = start.clone().utc().valueOf();
                endTimestamp = end.clone().utc().valueOf();
                break;
            case TimerType.Recurring:
                startTimestamp = start.clone().utc().startOf('day').valueOf();
                endTimestamp = end.clone().utc().endOf('day').valueOf();
                recurringResetTimerType = this.state.recurringResetTimerType;
                recurringStartTimestamp = recurringStart.clone().utc().valueOf() - recurringStart.clone().utc().startOf('day').valueOf();
                recurringDuration = this.state.recurringDuration * 60 * 1000;
                switch (this.state.recurringResetTimerType) {
                    case RecurringResetTimerType.Daily:
                        recurringDailyDays = this.state.recurringDailyDays;
                        break;
                    case RecurringResetTimerType.Weekly:
                        recurringWeeklyDayStart = this.state.recurringWeeklyDayStart;
                        break;
                }
                break;
        }
        const virtualCurrencyPricesData = isRealMoney ? null : virtualCurrencyPrices;

        if (type === 'Create' && onCreate !== undefined) {
            onCreate(
                displayName,
                metadata,
                productId,
                purchaseLimit,
                selectedSegmentIds,
                items,
                virtualCurrencyPricesData,
                startTimestamp,
                endTimestamp,
                timerType,
                recurringResetTimerType,
                recurringStartTimestamp,
                recurringDuration,
                recurringDailyDays,
                recurringWeeklyDayStart,
            );
        }
        else if (onEdit !== undefined) {
            onEdit(
                id,
                displayName,
                metadata,
                productId,
                purchaseLimit,
                selectedSegmentIds,
                items,
                virtualCurrencyPricesData,
                startTimestamp,
                endTimestamp,
                timerType,
                recurringResetTimerType,
                recurringStartTimestamp,
                recurringDuration,
                recurringDailyDays,
                recurringWeeklyDayStart,
            );
        }
        this.setState(this.initState());
    };

    private onCancel = async () => {
        this.props.onCancel();
    };

    private validate = () => {
        return true;
    }

    private handleDrop = (file: File) => this.setState({ file });

    private onChangeMetadata = (metadata: string) => this.setState({ metadata });

    private onChangeDisplayName = (displayName: string) => this.setState({ displayName });

    private onChangePurchaseLimit = (purchaseLimit: number) => this.setState({ purchaseLimit });

    private onChangeProductId = (productId: string) => this.setState({ productId });

    private handleStartChange = (start: Moment) => this.setState({ start });

    private handleEndChange = (end: Moment) => this.setState({ end });

    private handleRecurringDurationChange = (recurringDuration: number) => this.setState({ recurringDuration });

    private handleRecurringResetTimerTypeChange = (recurringResetTimerType: number) => this.setState({ recurringResetTimerType });

    private handleRecurringDailyDaysChange = (recurringDailyDays: number[] | null) => this.setState({ recurringDailyDays });

    private handleRecurringWeeklyDayStartChange = (recurringWeeklyDayStart: number) => this.setState({ recurringWeeklyDayStart });

    private handleRecurringStartChange = (recurringStart: Moment) => this.setState({ recurringStart });

    private changeIsRealMoney = (event: React.ChangeEvent<HTMLInputElement>) => this.setState({ isRealMoney: event.target.checked });

    private openSelectSegmentDialog = () => this.setState({ selectSegmentDialogOpen: true });

    private closeSelectSegmentDialog = () => this.setState({ selectSegmentDialogOpen: false });

    private onSelectSegment = (id: string) => this.setState( { selectedSegmentIds: [ ...this.state.selectedSegmentIds, id ], selectSegmentDialogOpen: false });
}

const StyledButton = styled(Button)(({
    color: '#0073bb',
    fontSize: 'smaller',
    borderColor: 'rgba(0, 0, 0, 0)',
    '&:disabled': {
        borderColor: 'rgba(0, 0, 0, 0)',
    },
    '&:hover': {
        backgroundColor: 'transparent',
    },
}));

const StyledTabPanel = styled(TabPanel)(({
    '&.MuiTabPanel-root': {
        padding: '10px 0px',
    }
}));
